import React, { useState } from 'react';
import MenuItem from './MenuItem';
import Moon from '../model/global/Moon';
import User from '../model/user/User';
import { UserRole } from '../model/user/UserRole';
import Sun from '../../common/model/global/Sun';
import {
  AppstoreOutlined,
  CloudSyncOutlined,
  DashboardOutlined,
  DeleteOutlined,
  PoweroffOutlined,
  SettingOutlined,
  ShareAltOutlined,
  DatabaseOutlined,
  TeamOutlined,
  FundOutlined,
  OrderedListOutlined,
  ProjectOutlined,
  ClusterOutlined,
  UsbOutlined,
  SaveOutlined,
  HddOutlined,
  EyeOutlined,
  AlertOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { LoginOutlined } from '@ant-design/icons/lib';
import Preference from '../model/preference/Preference';
import Lang from '../model/global/Lang';
import HttpUtil from '../util/HttpUtil';
import MessageBoxUtil from '../util/MessageBoxUtil';

export default class MenuManager {
  // 单例模式
  private static singleton: MenuManager;

  // 用于存储所有菜单项的展开状态
  private expandedKeys: Set<string> = new Set();

  // 存储最新的偏好设置
  private latestPreference: Preference | null = null;

  constructor() {}

  static getSingleton(): MenuManager {
    if (!MenuManager.singleton) {
      MenuManager.singleton = new MenuManager();
    }
    return MenuManager.singleton;
  }

  /**
   * 获取最新的偏好设置
   */
  private fetchLatestPreference(callback: (preference: Preference) => void) {
    // 如果有缓存的偏好设置，直接使用
    if (this.latestPreference) {
      callback(this.latestPreference);
      return;
    }

    // 从服务器获取最新的偏好设置
    HttpUtil.httpGet('/api/preference/fetch', {}, (response: any) => {
      const preferenceData = response.data?.data;
      if (preferenceData) {
        // 更新本地缓存的偏好设置
        this.latestPreference = preferenceData;
        // 同时更新 Moon 中的偏好设置，确保数据一致性
        Moon.getSingleton().preference = preferenceData;
        callback(preferenceData);
      } else {
        // 如果获取失败，使用 Moon 中的默认设置
        console.warn('Failed to fetch preference, using cached data');
        callback(Moon.getSingleton().preference);
      }
    }, (err: any) => {
      console.error('Error fetching preference:', err);
      // 出错时使用 Moon 中的默认设置
      callback(Moon.getSingleton().preference);
    });
  }

  /**
   * 清除缓存的偏好设置（在偏好设置更新后调用）
   */
  clearCachedPreference() {
    this.latestPreference = null;
  }

  /**
   * 获取当前高亮的菜单
   */
  getSelectedKeys(): string[] {
    return this.getMenuItems()
      .filter((menuItem: MenuItem) => menuItem.active)
      .map((menuItem: MenuItem) => menuItem.url);
  }

  /**
   * 高亮某个菜单
   */
  selectMenu(url: string) {
    //console.log(`MenuManager: 高亮菜单 ${url}`);
    this.getMenuItems().forEach((menuItem: MenuItem) => {
      menuItem.active = menuItem.url === url;
    });
  }

  /**
   * 切换菜单项的展开状态
   */
  toggleMenuItemExpansion(key: string) {
    if (this.expandedKeys.has(key)) {
      this.expandedKeys.delete(key); // 如果已经展开，则关闭
    } else {
      this.expandedKeys.add(key); // 如果未展开，则展开
    }
  }

  getExpandedKeys(): Set<string> {
    return this.expandedKeys;
  }

  /**
   * 检查某个菜单项是否展开
   */
  isMenuItemExpanded(key: string): boolean {
    return this.expandedKeys.has(key);
  }

  /**
   * 获取所有菜单项（异步版本）
   */
  getMenuItemsAsync(callback: (menuItems: MenuItem[]) => void) {
    this.fetchLatestPreference((preference: Preference) => {
      const menuItems = this.buildMenuItems(preference);
      callback(menuItems);
    });
  }

  /**
   * 获取所有菜单项（同步版本，保持向后兼容）
   */
  getMenuItems(): MenuItem[] {
    const user: User = Moon.getSingleton().user;
    const preference: Preference = Moon.getSingleton().preference;
    return this.buildMenuItems(preference);
  }

  /**
   * 构建菜单项
   */
  private buildMenuItems(preference: Preference): MenuItem[] {
    const user: User = Moon.getSingleton().user;

    let menuItems: MenuItem[] = [];

    if (!preference.installed) {
      menuItems = [
        new MenuItem(
          Lang.t('layout.install'),
          '/install/index',
          <SettingOutlined />
        ),
      ];
    } else if (user.role === UserRole.GUEST) {
      menuItems = [
        new MenuItem(Lang.t('user.login'), '/user/login', <LoginOutlined />),
      ];
    } else if (user.role === UserRole.AUDIT) {
      // 审计角色：只显示管理后台菜单下的系统日志和文件日志
      // 创建文件日志菜单项
      const fileslogs = new MenuItem(
        Lang.t('layout.fileslog'),
        '/fileslog/Listview',
        <DatabaseOutlined />
      );
      // 创建系统日志菜单项
      const systemLogs = new MenuItem(
        Lang.t('layout.systemlog'),
        '/syslog/SystemLogList',
        <HistoryOutlined />
      );
      // 创建管理后台父菜单，只包含日志子菜单
      const managementPage = new MenuItem(
        Lang.t('layout.managementPage'),
        '',
        <DashboardOutlined />,
        [fileslogs, systemLogs],
        'management'
      );
      menuItems.push(managementPage);
      // 添加退出登录菜单
      menuItems.push(
        new MenuItem(
          Lang.t('layout.logout'),
          '/user/logout',
          <PoweroffOutlined />
        )
      );
    } else {
      // 添加 FilesCirculationPage 菜单
      const FilesCirculationPage = new MenuItem(
        Lang.t('layout.FilesCirculation'),
        '',
        <ProjectOutlined />,
        [],
        'FilesCirculation'
      );

      // 添加 managementPage 菜单
      const managementPage = new MenuItem(
        Lang.t('layout.managementPage'),
        '',
        <DashboardOutlined />,
        [],
        'management'
      );

      // 创建子菜单
      const allfiles = new MenuItem(
        Lang.t('layout.allFiles'),
        '/matter/list',
        <AppstoreOutlined />
      );

      const showSpace = preference.ctrlMod !== undefined && 
                        preference.ctrlMod !== null && 
                        preference.ctrlMod !== 0;
      
      const space = showSpace ? new MenuItem(
        Lang.t('layout.space'),
        '/space',
        <CloudSyncOutlined />
      ) : null;

      // 可移动磁盘文件浏览器菜单项 - 使用独立的菜单项
      const removableFileBrowser = new MenuItem(
        Lang.t('layout.removableFileBrowser'),
        '/removable/list',
        <UsbOutlined />
      );

      const share = new MenuItem(
        Lang.t('layout.myShare'),
        '/share/list',
        <ShareAltOutlined />
      );

      const bin = new MenuItem(
        Lang.t('layout.bin'),
        '/bin/list',
        <DeleteOutlined />
      );

      const statistics = new MenuItem(
        Lang.t('layout.dashboard'),
        '/dashboard/index',
        <FundOutlined />
      );

      const platformSettings = new MenuItem(
        Lang.t('layout.setting'),
        '/preference/index',
        <SettingOutlined />
      );

      const UserSettings = new MenuItem(
        Lang.t('layout.users'),
        `${Sun.getSingleton().isMobile ? '/mobile' : ''}/user/list`,
        <TeamOutlined />
      );
// --- 2. 新增：构建 "在线用户" 菜单项 ---
      // 注意：这里使用 EyeOutlined 图标，如果您想用别的图标（如 UserOutlined），请确保已导入
      const OnlineUsers = new MenuItem(
        Lang.t('layout.onlineUsers') || '在线用户', // 语言包 key
        '/user/online/list',                        // 对应的路由路径
        <EyeOutlined />,                            // 图标
        [],                                         // 子菜单（无）
        'online-users'                              // 唯一 key
      );
      const DeptSettings = new MenuItem(
        Lang.t('layout.departments'),
        `${Sun.getSingleton().isMobile ? '/mobile' : ''}/department/list`,
        <ClusterOutlined />
      );

      const Fileslogs = new MenuItem(
        Lang.t('layout.fileslog'),
        '/fileslog/Listview',
        <DatabaseOutlined />
      );

      // 新增：系统日志菜单项
      const SystemLogs = new MenuItem(
        Lang.t('layout.systemlog'),
        '/syslog/SystemLogList',
        <HistoryOutlined />
      );

      // 构建 FilesCirculationPage 的子菜单
      const filesCirculationChildren: MenuItem[] = [];
      
      // 根据 ctrlMod 的值控制菜单显示
      const ctrlMod = preference.ctrlMod || 0;
      
      switch (ctrlMod) {
        case 1:
          // ctrlMod == 1: 不显示"可移动磁盘"子菜单，显示其他子菜单
          filesCirculationChildren.push(allfiles);
          if (space) {
            filesCirculationChildren.push(space);
          }
          filesCirculationChildren.push(share, bin);
          //console.log('MenuManager: ctrlMod=1, 不显示可移动磁盘菜单');
          break;
          
        case 2:
          // ctrlMod == 2: 显示所有子菜单
          filesCirculationChildren.push(allfiles);
          if (space) {
            filesCirculationChildren.push(space);
          }
          filesCirculationChildren.push(removableFileBrowser, share, bin);
         // console.log('MenuManager: ctrlMod=2, 显示所有子菜单');
          break;
          
        case 3:
          // ctrlMod == 3: 只显示"可移动磁盘"子菜单
          filesCirculationChildren.push(removableFileBrowser);
          //console.log('MenuManager: ctrlMod=3, 只显示可移动磁盘菜单');
          break;
          
        default:
          // 默认情况: 显示所有子菜单
          filesCirculationChildren.push(allfiles);
          if (space) {
            filesCirculationChildren.push(space);
          }
          filesCirculationChildren.push(removableFileBrowser, share, bin);
       //   console.log(`MenuManager: ctrlMod=${ctrlMod}, 使用默认菜单配置`);
          break;
      }

      // 将子菜单添加到父菜单
      FilesCirculationPage.children = filesCirculationChildren;
      
      // 构建管理页面子菜单，系统日志与文件日志并列
      const managementChildren: MenuItem[] = [
        statistics, 
        platformSettings, 
        UserSettings, 
        OnlineUsers,        // 👈 插在这里
        DeptSettings, 
        Fileslogs,
        SystemLogs  // 新增系统日志
      ];
      
      managementPage.children = managementChildren;
      
      menuItems.push(FilesCirculationPage);
      
      // 将带子菜单的菜单添加到菜单项中
      if (user.role === UserRole.ADMINISTRATOR) {
        menuItems.push(managementPage);
      }

      menuItems.push(
        new MenuItem(
          Lang.t('layout.logout'),
          '/user/logout',
          <PoweroffOutlined />
        )
      );
    }

    // 设置每个菜单项的展开状态
    menuItems.forEach((menuItem) => {
      menuItem.isExpanded = this.isMenuItemExpanded(menuItem.key);
    });

    // 调试输出生成的菜单项
    //console.log('MenuManager: 生成的菜单项:', menuItems);
    //console.log('MenuManager: 当前 ctrlMod 值:', preference.ctrlMod);
    //console.log('MenuManager: 可移动磁盘文件浏览器路径:', '/removable/list');

    return menuItems;
  }

  /**
   * 查找菜单项并高亮
   */
  findAndSelectMenuByPath(path: string): boolean {
    const menuItems = this.getMenuItems();
    let found = false;
    
    const searchMenu = (items: MenuItem[]) => {
      for (const item of items) {
        if (item.url === path) {
          item.active = true;
          found = true;
        } else {
          item.active = false;
        }
        
        if (item.children && item.children.length > 0) {
          searchMenu(item.children);
        }
      }
    };
    
    searchMenu(menuItems);
    
   /* if (found) {
      console.log(`MenuManager: 已高亮菜单项: ${path}`);
    } else {
      console.log(`MenuManager: 未找到匹配的菜单项: ${path}`);
    }*/
    
    return found;
  }
}